/**
 * The v2 presentation editor renders a *mock* of the customer nav ("the white header") inline at the
 * top of the canvas. The canvas column is clamped to 1080px, so without help the white bar is
 * exactly as wide as the column — which puts its left/right edges directly on the hamburger icon and
 * the estimate button, making it read as a box around those two buttons instead of a page header
 * (#22068).
 *
 * The real customer nav is `position: fixed` and spans the whole viewport while its toolbar keeps
 * the buttons over the 1080 content column. To reproduce that in the editor the bar has to bleed out
 * of the column and across the whole editor canvas — from the right edge of the fixed sidebar to the
 * right edge of the document.
 *
 * That bleed can't be expressed in pure CSS: `100vw` includes the classic scrollbar (which would add
 * horizontal page overflow), and the column is *not* centered in the canvas — `BasePage` is
 * `width: 95%; max-width: 1440px; margin: 0 auto` while the sidebar is `position: fixed; left: 0`,
 * so the two gutters are unequal and depend on the viewport width. So the editor measures the column
 * and publishes the two gutters as CSS custom properties, which the nav's styles consume. Everywhere
 * the properties are absent (settings dialogs, storybook, the real customer view) they fall back to
 * `0px` and nothing changes.
 */

import type { CSSProperties } from 'react'

export const PRESENTATION_NAV_BLEED_LEFT_VAR = '--presentation-nav-bleed-left'
export const PRESENTATION_NAV_BLEED_RIGHT_VAR = '--presentation-nav-bleed-right'

export interface PresentationNavBleed {
  left: number
  right: number
}

/** Shared by every no-bleed path, so it's frozen — callers must never treat a result as mutable. */
const NO_BLEED: PresentationNavBleed = Object.freeze({ left: 0, right: 0 })

/**
 * How far (in px) the presentation canvas column sits inside the editor canvas on each side.
 *
 * @param canvasRect viewport-relative bounds of the canvas column (a `DOMRect` works as-is)
 * @param sidebarWidth width of the fixed editor sidebar — the canvas starts at its right edge
 * @param documentWidth `document.documentElement.clientWidth`, which excludes the scrollbar
 * @param disabled when true (e.g. preview mode) no bleed is applied
 */
export function getPresentationNavBleed(args: {
  canvasRect?: { left: number; right: number }
  sidebarWidth: number
  documentWidth: number
  disabled?: boolean
}): PresentationNavBleed {
  const { canvasRect, sidebarWidth, documentWidth, disabled } = args

  if (disabled || !canvasRect) {
    return NO_BLEED
  }

  const { left, right } = canvasRect

  // an unmounted/hidden column measures as a zero-width rect — don't bleed off of a bogus measurement
  if (!(right > left) || !(documentWidth > 0)) {
    return NO_BLEED
  }

  return {
    left: clampGutter(left - sidebarWidth),
    right: clampGutter(documentWidth - right)
  }
}

/**
 * The measured bleed as inline style custom properties, so callers never have to spell the variable
 * names themselves. Spread onto the canvas column's `style`.
 */
export function getPresentationNavBleedStyle(bleed?: PresentationNavBleed): CSSProperties {
  const { left, right } = bleed ?? NO_BLEED

  // The `as CSSProperties` below is load-bearing: CSSProperties has no index signature for custom
  // properties, so `--*` keys don't typecheck — but React does pass them straight through to the
  // DOM node's style, which is the whole point of this helper.
  return {
    [PRESENTATION_NAV_BLEED_LEFT_VAR]: `${left}px`,
    [PRESENTATION_NAV_BLEED_RIGHT_VAR]: `${right}px`
  } as CSSProperties
}

/** Negative gutters (mid-transition, or a column wider than the document) would push the bar inward. */
function clampGutter(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0
  }

  return Math.round(value)
}
